import { useEffect, useRef, useState } from 'react'
import * as customersApi from '@/api/customers'
import { RichTextBlock } from '@/components/RichTextBlock'
import { StructuredTable } from '@/components/StructuredTable'
import {
  blockDisplayTitle,
  canMoveBlockRow,
  defaultBlockTitle,
  defaultLayoutForCategory,
  moveBlockRow,
  newBlockId,
  nextBlockY,
  resolveOverlaps,
} from '@/lib/pageLayout'

const DEFAULT_TABLE_COLUMNS = [
  { label: 'Name', input_type: 'text' },
  { label: 'URL', input_type: 'url' },
  { label: 'Password', input_type: 'secret' },
]

function serializeLayoutBlocks(blocks, categoryColumns = []) {
  return blocks.map((b) => ({
    id: b.id,
    type: b.type,
    title: blockDisplayTitle(b),
    x: Number(Number(b.x).toFixed(2)),
    y: Number(Number(b.y).toFixed(2)),
    w: Number(Number(b.w).toFixed(2)),
    content_html: b.type === 'rich' && b.id !== 'main-rich' ? b.content_html || '' : null,
    columns:
      b.type === 'table'
        ? (b.columns?.length ? b.columns : categoryColumns || []).map((c) => ({
            label: c.label,
            key: c.key || undefined,
            input_type: c.input_type || 'text',
            wide: Boolean(c.wide),
          }))
        : null,
  }))
}

/**
 * Freeform page canvas — drag to move, side-by-side rich text, no overlapping blocks.
 */
export function PageCanvas({
  customerId,
  category,
  canEdit,
  onCategoryUpdated,
}) {
  const [layoutEditing, setLayoutEditing] = useState(false)
  const [selectedBlockId, setSelectedBlockId] = useState(null)
  const [layout, setLayout] = useState(() => defaultLayoutForCategory(category))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [drag, setDrag] = useState(null)
  const [heights, setHeights] = useState({})
  const [editingTitleId, setEditingTitleId] = useState(null)
  const [titleDraft, setTitleDraft] = useState('')
  const canvasRef = useRef(null)
  const blockEls = useRef({})
  const layoutHeightsRef = useRef({})
  const layoutEditingRef = useRef(false)

  useEffect(() => {
    setLayout(defaultLayoutForCategory(category))
    setLayoutEditing(false)
    setSelectedBlockId(null)
    layoutEditingRef.current = false
    layoutHeightsRef.current = {}
    setError('')
  }, [category.id, category.slug])

  // Sync layout from server when not mid-edit (avoids wiping removals / add-table
  // when category.columns updates mid-session).
  useEffect(() => {
    if (layoutEditingRef.current) return
    setLayout(defaultLayoutForCategory(category))
  }, [category.page_layout, category.columns, category.kind])

  useEffect(() => {
    layoutEditingRef.current = layoutEditing
  }, [layoutEditing])

  // Measure block heights so collision checks stay accurate
  useEffect(() => {
    const observers = []
    layout.blocks.forEach((block) => {
      const el = blockEls.current[block.id]
      if (!el || typeof ResizeObserver === 'undefined') return
      const ro = new ResizeObserver((entries) => {
        if (layoutEditingRef.current) return
        const h = entries[0]?.contentRect?.height
        if (!h) return
        setHeights((prev) =>
          prev[block.id] === Math.round(h) ? prev : { ...prev, [block.id]: Math.round(h) },
        )
      })
      ro.observe(el)
      observers.push(ro)
    })
    return () => observers.forEach((ro) => ro.disconnect())
  }, [layout.blocks])

  // Pack vertical gaps once real heights are known (view mode only)
  useEffect(() => {
    if (layoutEditing || drag) return
    if (!Object.keys(heights).length) return
    setLayout((prev) => {
      const packed = resolveOverlaps(prev.blocks, heights)
      if (packed.length !== prev.blocks.length) {
        return { ...prev, blocks: packed }
      }
      const byId = Object.fromEntries(prev.blocks.map((b) => [b.id, b]))
      const same = packed.every((b) => {
        const o = byId[b.id]
        return (
          o &&
          Math.abs((Number(o.y) || 0) - (Number(b.y) || 0)) < 1 &&
          Math.abs((Number(o.x) || 0) - (Number(b.x) || 0)) < 0.1 &&
          Math.abs((Number(o.w) || 0) - (Number(b.w) || 0)) < 0.1
        )
      })
      if (same) return prev
      return { ...prev, blocks: packed }
    })
  }, [heights, layoutEditing, drag])

  useEffect(() => {
    if (!drag) return undefined
    const overlapHeights = layoutHeightsRef.current

    function onMove(e) {
      const canvas = canvasRef.current
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      const dxPct = ((e.clientX - drag.startX) / rect.width) * 100
      const dyPx = e.clientY - drag.startY
      let nextX = drag.originX + dxPct
      let nextY = drag.originY + dyPx
      nextX = Math.max(0, Math.min(100 - drag.blockW, nextX))
      nextY = Math.max(0, Math.min(7500, nextY))
      setLayout((prev) => ({
        ...prev,
        blocks: prev.blocks.map((b) =>
          b.id === drag.blockId ? { ...b, x: nextX, y: nextY } : b,
        ),
      }))
    }

    function onUp() {
      setLayout((prev) => ({
        ...prev,
        blocks: resolveOverlaps(prev.blocks, overlapHeights),
      }))
      setDrag(null)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [drag])

  function overlapHeights() {
    return layoutEditing ? layoutHeightsRef.current : heights
  }

  function beginLayoutEdit() {
    layoutHeightsRef.current = { ...heights }
    layoutEditingRef.current = true
    setSelectedBlockId(layout.blocks[0]?.id || null)
    setLayoutEditing(true)
  }

  function startDrag(e, block) {
    if (!layoutEditing) return
    e.preventDefault()
    e.stopPropagation()
    setSelectedBlockId(block.id)
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId)
    } catch {
      // ignore
    }
    setDrag({
      blockId: block.id,
      startX: e.clientX,
      startY: e.clientY,
      originX: Number(block.x) || 0,
      originY: Number(block.y) || 0,
      blockW: Number(block.w) || 100,
    })
  }

  function updateInlineHtml(blockId, html) {
    setLayout((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) =>
        b.id === blockId ? { ...b, content_html: html } : b,
      ),
    }))
  }

  function setBlockWidth(blockId, w) {
    setLayout((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) => {
        if (b.id !== blockId) return b
        const width = Math.max(20, Math.min(100, w))
        const x = Math.min(Number(b.x) || 0, 100 - width)
        return { ...b, w: width, x }
      }),
    }))
  }

  function finishWidthAdjust() {
    setLayout((prev) => ({
      ...prev,
      blocks: resolveOverlaps(prev.blocks, layoutHeightsRef.current),
    }))
  }

  function moveBlockVertical(blockId, direction) {
    if (!layoutEditing) return
    setSelectedBlockId(blockId)
    setLayout((prev) => ({
      ...prev,
      blocks: moveBlockRow(prev.blocks, blockId, direction, layoutHeightsRef.current),
    }))
  }

  function addRichBlock() {
    setError('')
    const id = newBlockId('rich')
    const h = overlapHeights()
    setLayout((prev) => ({
      ...prev,
      blocks: resolveOverlaps(
        [
          ...prev.blocks,
          {
            id,
            type: 'rich',
            title: defaultBlockTitle('rich'),
            x: 0,
            y: nextBlockY(prev.blocks, h),
            w: 100,
            content_html: '<p></p>',
          },
        ],
        h,
      ),
    }))
    layoutHeightsRef.current = { ...layoutHeightsRef.current, [id]: 220 }
    setSelectedBlockId(id)
  }

  async function addTableBlock() {
    setError('')
    const tableId = newBlockId('table')
    const blockColumns = DEFAULT_TABLE_COLUMNS.map((c) => ({ ...c }))
    // Fold columns into the same save as page_layout so we never mid-edit
    // bump category.columns alone (that used to reset the canvas).
    const needsCategoryColumns =
      !category.columns?.length && !layout.blocks.some((b) => b.type === 'table')
    const h = overlapHeights()
    const nextBlocks = resolveOverlaps(
      [
        ...layout.blocks,
        {
          id: tableId,
          type: 'table',
          title: defaultBlockTitle('table'),
          x: 0,
          y: nextBlockY(layout.blocks, h),
          w: 100,
          columns: blockColumns,
        },
      ],
      h,
    )
    layoutHeightsRef.current = { ...layoutHeightsRef.current, [tableId]: 260 }
    setSelectedBlockId(tableId)
    setLayout((prev) => ({ ...prev, blocks: nextBlocks }))
    try {
      setSaving(true)
      const payload = {
        version: 1,
        blocks: serializeLayoutBlocks(nextBlocks, category.columns),
      }
      const body = { page_layout: payload }
      if (needsCategoryColumns) {
        body.columns = DEFAULT_TABLE_COLUMNS
      }
      const updated = await customersApi.updateCategory(customerId, category.slug, body)
      onCategoryUpdated?.(updated)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not save new data table.',
      )
    } finally {
      setSaving(false)
    }
  }

  async function saveTableColumns(blockId, columns) {
    const tidied = resolveOverlaps(
      layout.blocks.map((b) => (b.id === blockId ? { ...b, columns } : b)),
      heights,
    )
    const payload = {
      version: 1,
      blocks: serializeLayoutBlocks(tidied, category.columns),
    }
    // Ensure the edited block keeps the new columns even if serialize fell back.
    payload.blocks = payload.blocks.map((b) =>
      b.id === blockId
        ? {
            ...b,
            columns: columns.map((c) => ({
              label: c.label,
              key: c.key || undefined,
              input_type: c.input_type || 'text',
              wide: Boolean(c.wide),
            })),
          }
        : b,
    )
    const body = { page_layout: payload }
    // Keep category.columns in sync for the first/default table (Manage categories).
    if (blockId === 'main-table' || tidied.filter((b) => b.type === 'table').length === 1) {
      body.columns = columns
    }
    const updated = await customersApi.updateCategory(customerId, category.slug, body)
    setLayout({ version: 1, blocks: tidied })
    onCategoryUpdated?.(updated)
  }

  async function persistLayoutBlocks(blocks, extra = {}) {
    const tidied = resolveOverlaps(blocks, overlapHeights())
    const payload = {
      version: 1,
      blocks: serializeLayoutBlocks(tidied, category.columns),
    }
    const body = { page_layout: payload, ...extra }
    // Clear legacy category.columns when no tables remain on a rich page.
    if (
      category.kind === 'rich' &&
      !tidied.some((b) => b.type === 'table') &&
      category.columns?.length
    ) {
      body.columns = []
    }
    const updated = await customersApi.updateCategory(customerId, category.slug, body)
    setLayout({ version: 1, blocks: tidied })
    onCategoryUpdated?.(updated)
    return tidied
  }

  async function saveBlockTitle(blockId, rawTitle) {
    const cleaned = String(rawTitle || '').trim()
    const block = layout.blocks.find((b) => b.id === blockId)
    if (!block) return
    const nextTitle = cleaned || defaultBlockTitle(block.type)
    if (blockDisplayTitle(block) === nextTitle) {
      setEditingTitleId(null)
      setTitleDraft('')
      return
    }
    setSaving(true)
    setError('')
    try {
      const nextBlocks = layout.blocks.map((b) =>
        b.id === blockId ? { ...b, title: nextTitle } : b,
      )
      await persistLayoutBlocks(nextBlocks)
      setEditingTitleId(null)
      setTitleDraft('')
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not save title.',
      )
    } finally {
      setSaving(false)
    }
  }

  async function removeBlock(blockId) {
    setError('')
    const removing = layout.blocks.find((b) => b.id === blockId)
    if (!removing) return

    let nextBlocks = layout.blocks.filter((b) => b.id !== blockId)
    const richLeft = nextBlocks.filter((b) => b.type === 'rich')

    // Removing main notes when other rich blocks remain — promote another so
    // page-level content storage still has a main-rich target.
    if (
      removing.id === 'main-rich' &&
      removing.type === 'rich' &&
      richLeft.length &&
      !richLeft.some((b) => b.id === 'main-rich')
    ) {
      const first = richLeft[0]
      nextBlocks = nextBlocks.map((b) =>
        b.id === first.id
          ? { ...b, id: 'main-rich', content_html: undefined }
          : b,
      )
    }

    nextBlocks = resolveOverlaps(nextBlocks, layoutHeightsRef.current)
    setSelectedBlockId((cur) => (cur === blockId ? nextBlocks[0]?.id || null : cur))
    setLayout((prev) => ({ ...prev, blocks: nextBlocks }))
    // Persist immediately so Remove sticks without needing Save layout.
    try {
      setSaving(true)
      await persistLayoutBlocks(nextBlocks)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not remove block.',
      )
      setLayout(defaultLayoutForCategory(category))
    } finally {
      setSaving(false)
    }
  }

  function tidyLayout() {
    setLayout((prev) => ({
      ...prev,
      blocks: resolveOverlaps(prev.blocks, layoutHeightsRef.current),
    }))
  }

  async function saveLayout() {
    setSaving(true)
    setError('')
    try {
      const tidied = await persistLayoutBlocks(layout.blocks)
      layoutEditingRef.current = false
      setLayoutEditing(false)
      setSelectedBlockId(null)
      setLayout({ version: 1, blocks: tidied })
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not save layout.',
      )
    } finally {
      setSaving(false)
    }
  }

  function cancelLayoutEdit() {
    setLayout(defaultLayoutForCategory(category))
    layoutEditingRef.current = false
    setLayoutEditing(false)
    setSelectedBlockId(null)
    setError('')
  }

  const activeHeights = overlapHeights()
  const selectedBlock = layout.blocks.find((b) => b.id === selectedBlockId)
  const canvasMinHeight = Math.max(
    480,
    ...layout.blocks.map(
      (b) => (Number(b.y) || 0) + (activeHeights[b.id] || heights[b.id] || 320) + 24,
    ),
  )

  return (
    <div className="mt-6 space-y-3">
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
          {!layoutEditing ? (
            <button
              type="button"
              onClick={beginLayoutEdit}
              className="rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
            >
              Edit layout
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={addRichBlock}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
              >
                Add rich text
              </button>
              <button
                type="button"
                onClick={addTableBlock}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
              >
                Add data table
              </button>
              <button
                type="button"
                onClick={tidyLayout}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
              >
                Fix overlaps
              </button>
              {selectedBlock && (
                <label
                  className="flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-xs text-sky-950"
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  Width ({blockDisplayTitle(selectedBlock)})
                  <input
                    type="range"
                    min={20}
                    max={100}
                    value={Math.round(selectedBlock.w)}
                    onChange={(e) =>
                      setBlockWidth(selectedBlock.id, Number(e.target.value))
                    }
                    onPointerUp={finishWidthAdjust}
                    onKeyUp={finishWidthAdjust}
                    className="w-40"
                  />
                  <span className="w-8 tabular-nums">
                    {Math.round(selectedBlock.w)}%
                  </span>
                </label>
              )}
              <span className="max-w-sm text-xs text-brand-muted">
                Use Up/Down to reorder sections (or drag :: Move). Remove deletes
                straight away. Select a block, then use Width here.
              </span>
              <div className="ml-auto flex gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={saveLayout}
                  className="rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save layout'}
                </button>
                <button
                  type="button"
                  onClick={cancelLayoutEdit}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {error && (
        <p className="text-sm text-red-700" role="alert">
          {error}
        </p>
      )}

      <div
        ref={canvasRef}
        className={[
          'relative w-full rounded-xl border border-slate-300 bg-slate-200/70 p-1',
          layoutEditing ? 'ring-2 ring-sky-300 ring-offset-2' : '',
        ].join(' ')}
        style={{ minHeight: `${canvasMinHeight}px` }}
      >
        {layout.blocks.length === 0 && (
          <p className="absolute inset-0 flex items-center justify-center text-sm text-brand-muted">
            {canEdit
              ? 'No blocks yet — click Edit layout, then add rich text or a data table.'
              : 'This page has no content yet.'}
          </p>
        )}

        {layout.blocks.map((block) => {
          const frozenH = layoutHeightsRef.current[block.id]
          return (
          <div
            key={block.id}
            ref={(el) => {
              if (el) blockEls.current[block.id] = el
              else delete blockEls.current[block.id]
            }}
            onClick={() => {
              if (layoutEditing) setSelectedBlockId(block.id)
            }}
            className={[
              'absolute rounded-lg border border-slate-300 bg-white shadow-sm',
              block.type === 'table' ? 'overflow-visible' : 'overflow-hidden',
              layoutEditing ? 'border-sky-500' : '',
              layoutEditing && selectedBlockId === block.id ? 'ring-2 ring-sky-500' : '',
              drag?.blockId === block.id ? 'z-30 shadow-md ring-2 ring-sky-400' : 'z-10',
            ].join(' ')}
            style={{
              left: `${block.x}%`,
              top: `${Number(block.y) || 0}px`,
              width: `${block.w}%`,
              minHeight: layoutEditing && frozenH ? `${frozenH}px` : '8rem',
              height: layoutEditing && frozenH ? `${frozenH}px` : undefined,
            }}
          >
            {!layoutEditing && (
              <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-100/90 px-3 py-1.5">
                {canEdit && editingTitleId === block.id ? (
                  <input
                    autoFocus
                    value={titleDraft}
                    disabled={saving}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onBlur={() => saveBlockTitle(block.id, titleDraft)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        e.currentTarget.blur()
                      }
                      if (e.key === 'Escape') {
                        setEditingTitleId(null)
                        setTitleDraft('')
                      }
                    }}
                    className="min-w-0 flex-1 rounded border border-slate-300 bg-white px-2 py-0.5 text-xs font-semibold text-brand outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                    aria-label="Section title"
                  />
                ) : (
                  <button
                    type="button"
                    disabled={!canEdit}
                    onClick={() => {
                      if (!canEdit) return
                      setEditingTitleId(block.id)
                      setTitleDraft(blockDisplayTitle(block))
                    }}
                    className={[
                      'min-w-0 truncate text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600',
                      canEdit ? 'hover:text-brand hover:underline' : 'cursor-default',
                    ].join(' ')}
                    title={canEdit ? 'Click to rename' : undefined}
                  >
                    {blockDisplayTitle(block)}
                  </button>
                )}
              </div>
            )}
            {layoutEditing && (
              <div
                className="flex cursor-grab items-center gap-2 overflow-x-auto whitespace-nowrap border-b border-sky-200 bg-sky-100 px-2 py-2 active:cursor-grabbing"
                onPointerDown={(e) => startDrag(e, block)}
              >
                <span
                  className="shrink-0 select-none rounded bg-sky-600 px-2 py-1 text-xs font-bold tracking-wide text-white"
                  aria-hidden
                >
                  :: Move
                </span>
                <span
                  className="flex shrink-0 items-center gap-1"
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="rounded border border-sky-400 bg-white px-2 py-1 text-xs font-semibold text-sky-950 hover:bg-sky-50 disabled:opacity-40"
                    title="Move section up"
                    aria-label={`Move ${blockDisplayTitle(block)} up`}
                    disabled={!canMoveBlockRow(layout.blocks, block.id, -1)}
                    onClick={() => moveBlockVertical(block.id, -1)}
                  >
                    Up
                  </button>
                  <button
                    type="button"
                    className="rounded border border-sky-400 bg-white px-2 py-1 text-xs font-semibold text-sky-950 hover:bg-sky-50 disabled:opacity-40"
                    title="Move section down"
                    aria-label={`Move ${blockDisplayTitle(block)} down`}
                    disabled={!canMoveBlockRow(layout.blocks, block.id, 1)}
                    onClick={() => moveBlockVertical(block.id, 1)}
                  >
                    Down
                  </button>
                </span>
                <label
                  className="flex min-w-0 flex-1 items-center gap-1 text-xs text-sky-950"
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  Title
                  <input
                    value={block.title ?? blockDisplayTitle(block)}
                    onChange={(e) =>
                      setLayout((prev) => ({
                        ...prev,
                        blocks: prev.blocks.map((b) =>
                          b.id === block.id ? { ...b, title: e.target.value } : b,
                        ),
                      }))
                    }
                    className="min-w-0 flex-1 rounded border border-sky-300 bg-white px-2 py-1 text-xs font-semibold text-brand"
                  />
                </label>
                <span className="shrink-0 text-xs capitalize text-sky-900/70">{block.type}</span>
                <button
                  type="button"
                  disabled={saving}
                  className="ml-auto shrink-0 text-xs font-medium text-red-700 underline disabled:opacity-50"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => removeBlock(block.id)}
                >
                  Remove
                </button>
              </div>
            )}
            <div className="p-2">
              {layoutEditing ? (
                <div
                  className="flex items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-xs text-brand-muted"
                  style={{
                    minHeight: frozenH ? `${Math.max(80, frozenH - 56)}px` : '10rem',
                  }}
                >
                  {block.type === 'rich'
                    ? 'Notes — edit after Save layout'
                    : 'Data table — edit after Save layout'}
                </div>
              ) : block.type === 'rich' ? (
                <RichTextBlock
                  customerId={customerId}
                  category={category}
                  canEdit={canEdit}
                  mode={block.id === 'main-rich' ? 'page' : 'inline'}
                  contentHtml={block.content_html || ''}
                  onInlineChange={(html) => updateInlineHtml(block.id, html)}
                  onSaveInline={
                    block.id === 'main-rich'
                      ? undefined
                      : async (html) => {
                          const blocks = layout.blocks.map((b) =>
                            b.id === block.id ? { ...b, content_html: html } : b,
                          )
                          await persistLayoutBlocks(blocks)
                        }
                  }
                />
              ) : (
                <StructuredTable
                  customerId={customerId}
                  category={category}
                  canEdit={canEdit}
                  tableKey={block.id}
                  columns={(block.columns?.length
                    ? block.columns
                    : category.columns || []
                  ).map((c) => {
                    if (c.key) return c
                    const match = (category.columns || []).find(
                      (cc) =>
                        (cc.label || '').toLowerCase() ===
                        (c.label || '').toLowerCase(),
                    )
                    return match ? { ...c, key: match.key } : c
                  })}
                  onColumnsChange={
                    canEdit
                      ? (cols) => saveTableColumns(block.id, cols)
                      : undefined
                  }
                />
              )}
            </div>
          </div>
          )
        })}
      </div>
    </div>
  )
}
